import { ButtonBase, Checkbox, IconButton } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbChecks, TbClock, TbPlaneArrival } from "react-icons/tb";
import { Fragment } from "react/jsx-runtime";

import { PASSENGER_STATUS } from "@/constants/status";
import { IPlanPassenger } from "@/types/tasks.types";

import { ShowStatusConfirmation } from "..";
import { PassengerContent } from "./PassengerContent";

const nextStatuses = {
    [PASSENGER_STATUS.PENDING]: PASSENGER_STATUS.WAITING,
    [PASSENGER_STATUS.WAITING]: PASSENGER_STATUS.PENDING,
};
const PassengerCard = ({
    passengerGroup,
    hasEditPermission,
    selectionList,
    handleSelection,
    handleStatusConfirmation,
}: {
    passengerGroup: IPlanPassenger<"api">[];
    hasEditPermission?: boolean;
    handleSelection: (newList: GridRowSelectionModel) => void;
    selectionList: GridRowSelectionModel;
    handleStatusConfirmation: (val?: ShowStatusConfirmation) => void;
}) => {
    const { t } = useTranslation();
    const { flightNumber, flightTime } = passengerGroup[0] || [];
    const allSelected = useMemo(
        () => passengerGroup.every((passenger) => selectionList.includes(passenger.id)),
        [selectionList, passengerGroup]
    );

    const handleAllSelection = () => {
        if (allSelected) {
            const newList = selectionList.filter((item) => passengerGroup.every((passenger) => passenger.id !== item));
            handleSelection(newList);
        } else {
            const newList = [...new Set([...selectionList, ...passengerGroup.map((passenger) => passenger.id)])];
            handleSelection(newList);
        }
    };

    const handleItemSelection = (id: string, add?: boolean) => {
        const index = selectionList.findIndex((i) => i === id);
        if (index >= 0) {
            if (add) return;
            const newList = selectionList.filter((i) => i !== id);
            handleSelection(newList);
        } else {
            handleSelection([...selectionList, id]);
        }
    };

    const handleClick = ({ status, id }: IPlanPassenger<"api">) => {
        const targetStatus = nextStatuses[status as PASSENGER_STATUS.PENDING | PASSENGER_STATUS.WAITING] as
            | PASSENGER_STATUS.PENDING
            | PASSENGER_STATUS.WAITING;
        if (!targetStatus || !status) {
            enqueueSnackbar(t("passengerStatusNotCompatible"), { variant: "info" });

            return;
        }
        handleItemSelection(id, true);

        handleStatusConfirmation([status as PASSENGER_STATUS.PENDING | PASSENGER_STATUS.WAITING, targetStatus]);
    };

    const isSelected = useCallback((id: string) => selectionList.includes(id), [selectionList]);
    const allArrived = passengerGroup.every((item) => item.status !== PASSENGER_STATUS.PENDING);
    return (
        <div
            className={`${allArrived ? "bg-green-100 text-green-800" : "bg-elitLightBlue/20 "}   p-2 rounded-sm rounded-b-lg flex flex-col `}
        >
            <div className="flex gap-2 items-center  pr-1 justify-between">
                <div className="flex items-center">
                    {hasEditPermission && (
                        <Checkbox
                            checked={allSelected}
                            onChange={handleAllSelection}
                            size="small"
                            className=" -translate-x-2"
                        />
                    )}

                    <div className="flex items-center  gap-1  ">
                        <TbPlaneArrival className=" lg:text-lg col-span-2" />
                        <span className="font-bold">{flightNumber}</span>
                        <div className="col-span-10  flex gap-2 items-start"></div>
                    </div>
                </div>
                <div className="flex items-center gap-1 ">
                    <span className="font-bold">{flightTime?.toFormat("HH:mm")}</span>
                    <TbClock className=" lg:text-lg col-span-2" />
                    <div className="col-span-10  flex gap-2 items-start"></div>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                {passengerGroup.map((passenger) => {
                    return (
                        <Fragment key={`passenger-${passenger.id}`}>
                            <div className="flex  items-center">
                                {hasEditPermission && (
                                    <Checkbox
                                        checked={isSelected(passenger.id)}
                                        onChange={() => handleItemSelection(passenger.id)}
                                        size="small"
                                        className=" -translate-x-2"
                                    />
                                )}
                                <ButtonBase component="div" className=" !grow -translate-x-2">
                                    <PassengerContent
                                        passenger={passenger}
                                        onClick={() => handleClick(passenger)}
                                        hasEditPermission={hasEditPermission}
                                        isArrived={passenger.status !== PASSENGER_STATUS.PENDING}
                                    >
                                        <div
                                            className={`flex justify-center items-center w-5 h-5 g rounded-full text-white border-solid ${passenger.status === PASSENGER_STATUS.PENDING ? "border border-elitDarkBlue" : " border border-elitDarkBlue/50"}`}
                                        >
                                            {hasEditPermission && (
                                                <IconButton onClick={() => handleClick(passenger)}>
                                                    {passenger.status !== PASSENGER_STATUS.PENDING && (
                                                        <TbChecks fontSize={16} className=" text-green-900" />
                                                    )}
                                                </IconButton>
                                            )}
                                        </div>
                                    </PassengerContent>
                                </ButtonBase>
                            </div>
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default PassengerCard;
